import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate, webLoadingTrue } from '../../services/navigate.service';
import PlusIcon from "../../images/PlusIcon";
import UploadIcon from "../../images/UploadIcon";
import CloseIcon from "../../images/CloseIcon";
import DatePicker from "react-datepicker";
import { validateNoSpecialCharsOrSpaces } from "../../validation/ValidationInput";
import { deleteEmployee, loadEmploy } from "../../services/employ.service";
import UploadIconV2 from "../../images/UploadIconV2";
import { useResponsive } from "../../hook/useResponsive";
import EmployeesIcon from "../../images/Employees";
import { getTransactionMonth } from "../../services/purchases.services";
import { importCsv } from "../../services/payrollPayments.service";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import PayrollPaymentModal from "../PayrollPayments/PayrollPaymentModal";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import TagStatus from "../../components/TagStatus";
import { ConfirmSheet } from "../../components/mobile";
import {
    ListColumn,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
    formatDayMonth,
    t,
} from "../../components/list";
interface EmployeeRow {
    id: string;
    employeeCode: string;
    firstName: string;
    lastName: string;
    employeeNo: string;
    emailAddress: string;
    startDate: string | null;
    endDate: string | null;
    status: { statusName: string } | null;
}
interface Country {
    id: string;
    code: string;
    name: string;
}
interface InforTable {
    title: string;
    path: string;
    desc: string;
}
interface EmployeesResponse {
    data: {
        data: EmployeeRow[];
        count: number
    }

}
/** `date` columns arrive as YYYY-MM-DD; build the local day so -UTC zones do not show the day before. */
const formatBusinessDate = (value: string | null | undefined): string => {
    const [y, m, d] = String(value ?? '').slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return '';
    return formatDayMonth(new Date(y, m - 1, d));
};
const Employees = () => {
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const preLanguage = localStorage.getItem("language");
    const [language, setLanguage] = useState<any>(null);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>("")
    const { isMobile, isTouchLayout } = useResponsive();
    const [startDate, setStartDate] = useState(new Date());
    const [countries, setCountries] = useState<Country[]>([]);
    const [formValues, setFormValues] = useState<any>();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [step, setStep] = useState<string>("1");
    const [fileName, setFileName] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const inforTable: any = {
        img: <EmployeesIcon />,
        title: language && language?.["No employees yet"] ? language?.["No employees yet"] : "No employees yet", path: "/employees/new", desc: language && language?.["Add a new employee to manage your team here."] ? language?.["Add a new employee to manage your team here."] : "Add a new employee to manage your team here."
    };
    const [showPayroll, setShowPayroll] = useState<boolean>(false);
    const [payrollFile, setPayrollFile] = useState<File | null>(null);
    const [listMonths, setListMonths] = useState<any[]>([]);
    const [selectedMonthId, setSelectedMonthId] = useState("");
    const [selectedMonthLabel, setSelectedMonthLabel] = useState("");
    const [payrollPaymentDate, setPayrollPaymentDate] = useState<Date | null>(null);
    const [isMonthOpen, setIsMonthOpen] = useState(false);
    const [payrollImportLoading, setPayrollImportLoading] = useState(false);
    const [payrollBatchIdAfterImport, setPayrollBatchIdAfterImport] = useState<string | null>(null);
    const payrollMonthRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);
    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };
    const handleStartDate = (value: any) => {
        setStartDate(value);
        setFormValues({ ...formValues, startTime: value });
    };
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };

    }, []);

    const handleFileChange = (e: any) => {
        const file = e.target.files?.[0];
        if (file && file.type === 'text/csv') {
            setFileName(file.name);
            setPayrollFile(file);
        } else {
            setFileName('');
            setPayrollFile(null);
            if (file) showToast({ message: 'Please upload a valid CSV file', type: 'error' });
        }
    };

    const handleDrop = (e: any) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && file.type === 'text/csv') {
            setFileName(file.name);
            setPayrollFile(file);
        } else {
            setFileName('');
            setPayrollFile(null);
            showToast({ message: 'Please upload a valid CSV file', type: 'error' });
        }
    };

    useEffect(() => {
        if (!showPayroll) return;
        const locationId = localStorage.getItem("locationId") || "";
        const companyId = localStorage.getItem("companyId") || "";
        getTransactionMonth(locationId, companyId)
            .then((res: any) => {
                const raw = res?.data ?? [];
                const filtered = (Array.isArray(raw) ? raw : [])
                    .filter((m: any) => !m.isDeleted)
                    .sort((a: any, b: any) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime());
                setListMonths(filtered);
                if (filtered.length > 0 && !selectedMonthId) {
                    const first = filtered[0];
                    setSelectedMonthId(first.id);
                    setSelectedMonthLabel(`${first.transactionYear} - ${first.monthSequence}`);
                }
            })
            .catch(() => {});
    }, [showPayroll]);

    const handleProcessPayroll = async () => {
        if (!payrollFile || !selectedMonthId) return;
        setPayrollImportLoading(true);
        webLoadingTrue();
        try {
            const paymentDateStr = payrollPaymentDate
                ? payrollPaymentDate.toISOString().slice(0, 10)
                : undefined;
            const result = await importCsv(payrollFile, {
                transactionMonthsId: selectedMonthId,
                paymentDate: paymentDateStr,
            });
            showToast({
                message: `Imported ${result.lineCount} rows. Total: ${Number(result.totalAmount).toFixed(2)}`,
                type: "success",
            });
            setPayrollBatchIdAfterImport(result.id);
        } catch (err: any) {
            showApiError(err, { language });
        } finally {
            setPayrollImportLoading(false);
            webLoadingFalse();
        }
    };

    const handleDragOver = (e: any) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    /** One page of employees; the API matches `searchValue` against the last name only. */
    const fetchEmployees = async (query: ListQuery) => {
        webLoadingFalse();
        const response: EmployeesResponse = await loadEmploy({
            page: query.page,
            limit: query.limit,
            searchValue: query.search.trim(),
            sort: "DESC",
        });
        return { rows: response?.data?.data ?? [], count: response?.data?.count ?? 0 };
    };
    const goToEmployee = (id: string) => webNavigate('/' + preLanguage + '/employees/' + id);
    const handleCountrySelect = (countryId: string, countryName: string) => {
        setFormValues((prev: any) => ({ ...prev, country: countryId }));
        setSearchTerm(countryName);
        setIsCountryDropdownOpen(false);
    };
    const handleShowDelete = (id: string) => {
        setShowDelete(true);
        setIdDelete(id)
    }
    const handleDeleteEmployee = async () => {
        try {
            const response = await deleteEmployee(idDelete);
        } catch (error) {
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
            setShowDelete(false)
        }
    }

    /* ---------------- List kit configuration ---------------- */

    const fullName = (e: EmployeeRow) => [e.firstName, e.lastName].filter(Boolean).join(' ');
    const columns: ListColumn<EmployeeRow>[] = [
        { key: 'employeeCode', label: 'Code', width: 110, value: e => e.employeeCode ?? '', mobile: 'subtitle' },
        {
            key: 'name',
            label: 'Name',
            width: 220,
            hideable: false,
            value: e => fullName(e),
            render: cells.primary(e => fullName(e)),
            mobile: 'title',
            onClick: e => goToEmployee(String(e.id)),
        },
        { key: 'employeeNo', label: 'Identification No', width: 160, value: e => e.employeeNo ?? '', mobile: 'sheet' },
        {
            key: 'emailAddress',
            label: 'Email',
            width: 220,
            value: e => e.emailAddress ?? '',
            // Long addresses wrap in the record sheet instead of widening it.
            mobileRender: e => <span style={{ overflowWrap: 'anywhere' }}>{e.emailAddress ?? ''}</span>,
            mobile: 'sheet',
        },
        { key: 'startDate', label: 'Starting Date', width: 130, value: e => formatBusinessDate(e.startDate), mobile: 'sheet' },
        { key: 'endDate', label: 'Expiration Date', width: 130, value: e => formatBusinessDate(e.endDate), mobile: 'sheet' },
        {
            key: 'status',
            label: 'Status',
            width: 120,
            value: e => e.status?.statusName ?? '',
            render: (e, ctx) => {
                const name = e.status?.statusName ?? '';
                return <TagStatus value={name} label={name ? t(ctx.language, name, name) : undefined} />;
            },
            mobile: 'trailing',
        },
    ];

    const rowActions: ListRowAction<EmployeeRow>[] = [
        {
            key: 'edit',
            label: 'Edit',
            icon: <EditIconV2 />,
            sheetIcon: 'inventory',
            onRun: e => goToEmployee(String(e.id)),
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIconV2 />,
            sheetIcon: 'close',
            danger: true,
            onRun: e => handleShowDelete(String(e.id)),
        },
    ];
    return (
        <>
            {/* Desktop is a flex column so the list fills the height left under the title row. */}
            <div
                className="h-full relative"
                style={{
                    height: '100%',
                    overflowY: isTouchLayout ? "auto" : undefined,
                    paddingBottom: isTouchLayout ? "24px" : undefined,
                    display: isTouchLayout ? undefined : 'flex',
                    flexDirection: isTouchLayout ? undefined : 'column',
                }}
            >
                <div className="lg:flex items-center justify-between" >
                    <p
                        style={{ color: '#222525' }}
                        className="text-[24px] leading-[130%] font-medium font-inter"
                    >
                        {language && language?.['Employees']
                            ? language?.['Employees']
                            : 'Employees'}
                    </p>
                    <div className="flex items-center" style={{ gap: '12px', marginTop: isMobile ? "20px" : '0px', flexWrap: 'wrap' }}>
                        <div
                            className="cursor-pointer flex items-center justify-center"
                            style={{
                                gap: '8px',
                                padding: '8px 24px',
                                backgroundColor: '#EBF0F0',
                                borderRadius: '30px',
                                width: "fit-content"
                            }}
                            onClick={() => { setShowPayroll(true) }}
                        >
                            <div className="w-full flex items-center gap-x-2">
                                <UploadIcon />
                                <p className="text-[14px] leading-[20px] font-inter" style={{ color: "#0F7679", fontWeight: '500' }}>{language && language?.["Upload payroll list"] ? language?.["Upload payroll list"] : "Upload payroll list"}</p>
                            </div>
                        </div>
                        <div
                            className="cursor-pointer flex items-center justify-center"
                            style={{
                                gap: '8px',
                                padding: "8px 24px 8px 16px",
                                backgroundColor: '#0F7679',
                                borderRadius: '30px',
                                width: "fit-content",
                            }}
                            onClick={() => {
                                webNavigate('/' + preLanguage + '/employees/new');
                            }}
                        >
                            <div className="w-6 h-6 flex items-center justify-center">
                                <PlusIcon />
                            </div>
                            <p
                                style={{ color: '#FFFFFF' }}
                                className="text-[14px] leading-[130%] font-medium font-inter"
                            >
                                {language && language?.['Add employee']
                                    ? language?.['Add employee']
                                    : 'Add employee'}
                            </p>
                        </div>
                    </div>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchEmployees}
                    searchPlaceholder="Search employees"
                    searchInputRef={searchInputRef}
                    rowActions={rowActions}
                    countNoun="employees"
                    emptyState={inforTable}
                />
            </div>
            {isTouchLayout ? (
                <ConfirmSheet
                    open={showDelete}
                    title={language && language?.["Delete employees?"] ? language?.["Delete employees?"] : "Delete employees?"}
                    message={language && language?.["This employees will be permanently deleted. Are you sure you want to continue?"] ? language?.["This employees will be permanently deleted. Are you sure you want to continue?"] : "This employees will be permanently deleted. Are you sure you want to continue?"}
                    confirmLabel={language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                    cancelLabel={language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                    onConfirm={handleDeleteEmployee}
                    onCancel={() => setShowDelete(false)}
                />
            ) : (
                <div
                    className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                        } transition-opacity duration-300 ease-in-out`}
                    style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100%" }}
                >
                    <div
                        className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
                            padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", transition: "transform 0.3s ease, opacity 0.3s ease",
                        }}
                    >
                        <div
                            className="flex items-center justify-between"
                        >
                            <p className="text-[16px] font-semibold font-inter text-main leading-[24px]">
                                {language && language?.["Delete employees?"] ? language?.["Delete employees?"] : "Delete employees?"}
                            </p>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="flex items-center justify-center cursor-pointer"
                                style={{
                                    width: "24px",
                                    height: "24px",
                                }}
                            >
                                <CloseIcon />
                            </div>
                        </div>
                        <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
                                {language && language?.["This employees will be permanently deleted. Are you sure you want to continue?"] ? language?.["This employees will be permanently deleted. Are you sure you want to continue?"] : "This employees will be permanently deleted. Are you sure you want to continue?"}
                            </p>
                            {/* <span className="text-main">[sale ID]</span> sale. */}
                        </div>
                        <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: '30px',
                                    padding: '10px 24px',
                                    border: '1px solid #C9D7D8',
                                    color: '#222525',
                                }}
                            >
                                {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                            </div>
                            <div
                                onClick={handleDeleteEmployee}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: '30px',
                                    padding: '10px 24px',
                                    backgroundColor: '#BA0E13',
                                    color: '#FFFFFF',
                                }}
                            >
                                {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className={`fixed inset-0 flex items-end justify-end duration-300`}
                style={{ height: "100vh", width: "100vw", backgroundColor: "#22252552", top: 0, left: 0, opacity: showPayroll ? 1 : 0, zIndex: "200", pointerEvents: showPayroll ? "auto" : "none" }}
            >
                <div style={{ backgroundColor: '#FFFFFF', borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px', padding: "24px", width: "80%", height: "100%", transform: showPayroll ? "translateX(0)" : "translateX(100%)", transition: "transform 0.3s ease-in-out" }} className="shadow_panel ">
                    <div className="flex items-ceter justify-between" >
                        <p style={{ fontSize: '20px', color: '#222525' }} className="font-inter font-semibold">Declaration of Payroll</p>
                        <div onClick={() => { setShowPayroll(false) }} className="flex items-center justify-center cursor-pointer" style={{ width: "24px", height: "24px" }}>
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="w-full bg-white h-full relative" style={{ marginTop: '24px' }}>
                        {
                            <>
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#222525', fontWeight: '500' }}>
                                                {language && language?.["Transaction month"] ? language?.["Transaction month"] : "Transaction month"}
                                            </p>
                                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>*</p>
                                        </div>
                                        <div ref={payrollMonthRef} className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setIsMonthOpen(!isMonthOpen)}
                                                className="w-full text-left border border-[#EBEBED] rounded-xl px-4 py-3 text-[14px] font-inter flex items-center justify-between bg-transparent"
                                                style={{ color: "#222525" }}
                                            >
                                                {selectedMonthLabel || (language && language?.["Select month"] ? language?.["Select month"] : "Select month")}
                                                <span className="text-[#4A5353]">▼</span>
                                            </button>
                                            {isMonthOpen && (
                                                <div className="absolute z-10 mt-1 w-full bg-white border border-[#EBEBED] rounded-xl shadow-lg max-h-60 overflow-auto">
                                                    {listMonths.map((m: any) => (
                                                        <button
                                                            key={m.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedMonthId(m.id);
                                                                setSelectedMonthLabel(`${m.transactionYear} - ${m.monthSequence}`);
                                                                setIsMonthOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-3 text-[14px] font-inter hover:bg-[#F5F7F7]"
                                                            style={{ color: "#222525" }}
                                                        >
                                                            {m.transactionYear} - {m.monthSequence}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-full space-y-2" style={{ marginTop: "20px" }}>
                                        <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#222525', fontWeight: '500' }}>
                                            {language && language?.["Payment date"] ? language?.["Payment date"] : "Payment date"}
                                            <span style={{ color: '#6A7878' }}> ({language && language?.["optional"] ? language?.["optional"] : "optional"})</span>
                                        </p>
                                        <DatePicker
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText={language && language?.["Select payment date"] ? language?.["Select payment date"] : "Select payment date"}
                                            className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
                                            selected={payrollPaymentDate}
                                            onChange={(d: Date | null) => setPayrollPaymentDate(d)}
                                        />
                                    </div>
                                    <div
                                        className="w-full flex items-center justify-center"
                                        style={{
                                            marginTop: "20px",
                                            padding: "32px 24px",
                                            backgroundColor: '#FFFFFF',
                                            border: `1px dashed ${isDragging ? '#3AC0C4' : '#DBE4E4'}`,
                                            borderRadius: '12px',
                                        }}
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                    >
                                        <div className="space-y-2 text-center">
                                            <div className="flex items-center justify-center">
                                                <UploadIconV2 />
                                            </div>
                                            <label htmlFor="fileInput" className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer" style={{ color: "#0F7679", marginTop: '8px' }}>
                                                {language && language?.["Upload CSV File"] ? language?.["Upload CSV File"] : "Upload CSV File"}
                                                <span style={{ color: '#4A5353' }} className="text-[14px] leading-[20px] font-inter">{fileName ? ` — ${fileName}` : " or drag & drop here"}</span>
                                            </label>

                                            <p style={{ fontSize: '12px', color: '#6A7878' }} className="leading-[22px] font-inter">CSV up to 10MB</p>
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleFileChange}
                                                style={{ display: 'none' }}
                                                id="fileInput"
                                            />
                                        </div>
                                    </div>
                                </>
                        }
                        <div className="absolute flex items-center gap-x-3" style={{ bottom: "48px", right: '0px' }}>
                            <div
                                onClick={() => { setShowPayroll(false); setPayrollBatchIdAfterImport(null); }}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter flex items-center justify-center"
                                style={{ width: "fit-content", borderRadius: "40px", padding: '10px 24px', border: "1px solid #C9D7D8", color: "#222525", fontWeight: '500' }}>
                                {language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
                            </div>
                            <div
                                onClick={handleProcessPayroll}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter flex items-center justify-center disabled:opacity-50"
                                style={{
                                    width: "fit-content",
                                    borderRadius: "40px",
                                    padding: '10px 24px',
                                    backgroundColor: "#0F7679",
                                    color: "#FFFFFF",
                                    fontWeight: '500',
                                    pointerEvents: !payrollFile || !selectedMonthId || payrollImportLoading ? 'none' : 'auto',
                                }}>
                                {payrollImportLoading ? (language && language?.["Processing..."] ? language?.["Processing..."] : "Processing...") : (language && language?.["Process"] ? language?.["Process"] : "Process")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {payrollBatchIdAfterImport && (
                <PayrollPaymentModal
                    batchId={payrollBatchIdAfterImport}
                    onClose={() => {
                        setPayrollBatchIdAfterImport(null);
                        setShowPayroll(false);
                        setFileName('');
                        setPayrollFile(null);
                    }}
                />
            )}
        </>
    )
};

export default Employees;